/**
 * ============================================================================
 * УТИЛИТЫ ДЛЯ РАБОТЫ С ДАТАМИ И ВРЕМЕНЕМ
 * ============================================================================
 * Функции для форматирования дат в различных форматах
 * Используется для отображения времени активности пользователей
 * ============================================================================
 */

/**
 * Форматирование даты в относительный формат ("2 часа назад", "3 дня назад")
 * @param {string|Date|null} dateInput - Дата для форматирования
 * @returns {string} Отформатированная строка
 */
export function formatRelativeTime(dateInput) {
	// Обработка null/undefined
	if (!dateInput) {
		return 'Никогда'
	}

	try {
		const date = new Date(dateInput)

		// Проверка валидности даты
		if (isNaN(date.getTime())) {
			console.warn('Невалидная дата:', dateInput)
			return 'Неизвестно'
		}

		const now = new Date()
		const diffMs = now - date
		const diffSeconds = Math.floor(diffMs / 1000)
		const diffMinutes = Math.floor(diffSeconds / 60)
		const diffHours = Math.floor(diffMinutes / 60)
		const diffDays = Math.floor(diffHours / 24)
		const diffMonths = Math.floor(diffDays / 30)
		const diffYears = Math.floor(diffDays / 365)

		// Будущая дата (не должно быть, но на всякий случай)
		if (diffMs < 0) {
			return 'Только что'
		}

		// Менее минуты
		if (diffSeconds < 60) {
			return 'Только что'
		}

		// Менее часа
		if (diffMinutes < 60) {
			return `${diffMinutes} ${pluralize(diffMinutes, 'минуту', 'минуты', 'минут')} назад`
		}

		// Менее суток
		if (diffHours < 24) {
			return `${diffHours} ${pluralize(diffHours, 'час', 'часа', 'часов')} назад`
		}

		// Менее недели
		if (diffDays < 7) {
			return `${diffDays} ${pluralize(diffDays, 'день', 'дня', 'дней')} назад`
		}

		// Менее месяца
		if (diffDays < 30) {
			const weeks = Math.floor(diffDays / 7)
			return `${weeks} ${pluralize(weeks, 'неделю', 'недели', 'недель')} назад`
		}

		// Менее года
		if (diffMonths < 12) {
			return `${diffMonths} ${pluralize(diffMonths, 'месяц', 'месяца', 'месяцев')} назад`
		}

		// Больше года
		return `${diffYears} ${pluralize(diffYears, 'год', 'года', 'лет')} назад`
	} catch (error) {
		console.error('Ошибка форматирования относительного времени:', error)
		return 'Неизвестно'
	}
}

/**
 * Форматирование даты в абсолютный формат ("15.12.2024 14:30")
 * @param {string|Date|null} dateInput - Дата для форматирования
 * @param {boolean} includeTime - Включать ли время (по умолчанию true)
 * @returns {string} Отформатированная строка
 */
export function formatAbsoluteTime(dateInput, includeTime = true) {
	// Обработка null/undefined
	if (!dateInput) {
		return 'Никогда'
	}

	try {
		const date = new Date(dateInput)

		// Проверка валидности даты
		if (isNaN(date.getTime())) {
			console.warn('Невалидная дата:', dateInput)
			return 'Неизвестно'
		}

		const options = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}

		if (includeTime) {
			options.hour = '2-digit'
			options.minute = '2-digit'
		}

		// Форматирование в русской локали
		return date.toLocaleString('ru-RU', options)
	} catch (error) {
		console.error('Ошибка форматирования абсолютного времени:', error)
		return 'Неизвестно'
	}
}

/**
 * Комбинированное форматирование: относительное для недавних дат, абсолютное для старых
 * @param {string|Date|null} dateInput - Дата для форматирования
 * @param {number} thresholdDays - Порог в днях для переключения на абсолютный формат (по умолчанию 7)
 * @returns {string} Отформатированная строка
 */
export function formatSmartTime(dateInput, thresholdDays = 7) {
	if (!dateInput) {
		return 'Никогда'
	}

	try {
		const date = new Date(dateInput)

		if (isNaN(date.getTime())) {
			return 'Неизвестно'
		}

		const now = new Date()
		const diffMs = now - date
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

		// Если дата недавняя - показываем относительное время
		if (diffDays < thresholdDays) {
			return formatRelativeTime(dateInput)
		}

		// Если дата старая - показываем абсолютное время
		return formatAbsoluteTime(dateInput)
	} catch (error) {
		console.error('Ошибка форматирования умного времени:', error)
		return 'Неизвестно'
	}
}

/**
 * Получение разницы в днях между датой и текущим моментом
 * @param {string|Date|null} dateInput - Дата для сравнения
 * @returns {number|null} Количество дней (null если дата невалидна)
 */
export function getDaysSince(dateInput) {
	if (!dateInput) {
		return null
	}

	try {
		const date = new Date(dateInput)

		if (isNaN(date.getTime())) {
			return null
		}

		const now = new Date()
		const diffMs = now - date
		return Math.floor(diffMs / (1000 * 60 * 60 * 24))
	} catch (error) {
		console.error('Ошибка вычисления разницы в днях:', error)
		return null
	}
}

/**
 * Проверка, была ли дата в течение последних N дней
 * @param {string|Date|null} dateInput - Дата для проверки
 * @param {number} days - Количество дней
 * @returns {boolean} true если дата в пределах указанного периода
 */
export function isWithinDays(dateInput, days) {
	const daysSince = getDaysSince(dateInput)

	if (daysSince === null) {
		return false
	}

	return daysSince <= days
}

/**
 * Вспомогательная функция для правильного склонения русских слов
 * @param {number} number - Число
 * @param {string} one - Форма для 1 (минута, час, день)
 * @param {string} few - Форма для 2-4 (минуты, часа, дня)
 * @param {string} many - Форма для 5+ (минут, часов, дней)
 * @returns {string} Правильная форма слова
 */
function pluralize(number, one, few, many) {
	const mod10 = number % 10
	const mod100 = number % 100

	if (mod10 === 1 && mod100 !== 11) {
		return one
	}

	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
		return few
	}

	return many
}

export default {
	formatRelativeTime,
	formatAbsoluteTime,
	formatSmartTime,
	getDaysSince,
	isWithinDays,
}

